import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { formatMoney } from '@shared/utils/formatMoney';
import { cn } from '@shared/utils/cn';
import { chargeNow, GUARANTEE_REQUIRES, type GuaranteeAdjustment } from '../api/guaranteeAdjustment';

interface GuaranteeChangeNoticeProps {
  adjustment: GuaranteeAdjustment | null;
  /** Set on the reservation-action link once it refused a change for an acceptance or a payment: the guest gives it from their reservations. */
  onOpenReservations?: () => void;
  /** Applied to the live region, which takes no space while it is empty. */
  className?: string;
  /** The id of the amount block, which names the acceptances that follow it. */
  amountId?: string;
}

/** The new guarantee amount when the guest must accept it, pay part of it now, or add a card by email; nothing otherwise. */
const GuaranteeChangeNotice = ({ adjustment, onOpenReservations, className, amountId }: GuaranteeChangeNoticeProps) => {
  const { t, i18n } = useTranslation('booking');
  const requires = adjustment?.requires;
  const charge = chargeNow(adjustment);
  const shown = requires === GUARANTEE_REQUIRES.ACCEPTANCE || requires === GUARANTEE_REQUIRES.CARD || charge ? adjustment : null;
  // A USD twin beside each amount of a quote in another currency, when that amount has one.
  const pricedInUsd = adjustment?.currency === 'USD';
  const amount = (fee: number, feeUsd: number) => (
    <span className="whitespace-nowrap">
      {formatMoney(fee, adjustment?.currency, i18n.language)}
      {!pricedInUsd && feeUsd > 0 && (
        <> <span className="font-normal text-muted-foreground">({formatMoney(feeUsd, 'USD', i18n.language)})</span></>
      )}
    </span>
  );

  return (
    <div aria-live="polite" className={cn('w-full empty:hidden', className)}>
      {(shown || onOpenReservations) && (
        <div id={amountId} className="bg-foreground/5 rounded-xl p-4 space-y-2 text-sm text-left">
          {shown && (
            <>
              <p className="text-foreground/70">{t(charge ? 'CONFIRM_GUARANTEE_DEPOSIT_AMOUNT' : 'CONFIRM_GUARANTEE_AMOUNT')}</p>
              <p className="flex flex-wrap items-center gap-x-2 font-semibold text-foreground">
                {shown.previousFee > 0 && amount(shown.previousFee, shown.previousFeeUsd)}
                {/* The arrow wraps with the new amount, never alone at the end of a line. */}
                <span className="inline-flex items-center gap-x-2">
                  {shown.previousFee > 0 && <ArrowRight size={16} aria-hidden="true" className="text-foreground/50" />}
                  {amount(shown.newFee, shown.newFeeUsd)}
                </span>
              </p>
              {requires === GUARANTEE_REQUIRES.CARD && (
                <p className="text-foreground/70">{t('GUARANTEE_CHANGE_CARD_BY_EMAIL')}</p>
              )}
              {charge && (
                <p className="flex flex-wrap items-baseline justify-between gap-x-3 border-t border-foreground/10 pt-2">
                  <span className="text-foreground/70">{t('GUARANTEE_CHANGE_PAY_NOW')}</span>
                  <span className="whitespace-nowrap font-semibold text-foreground">{formatMoney(charge.amount, charge.currency, i18n.language)}</span>
                </p>
              )}
              {charge && charge.surchargePercent > 0 && (
                <p className="text-xs text-foreground/70">{t('GUARANTEE_CHANGE_PAY_NOW_SURCHARGE', { percent: charge.surchargePercent })}</p>
              )}
            </>
          )}
          {onOpenReservations && (
            <>
              <p className="text-foreground/70">{t(charge ? 'GUARANTEE_CHANGE_PAY_IN_RESERVATIONS' : 'GUARANTEE_CHANGE_ACCEPT_IN_RESERVATIONS')}</p>
              <Button
                className="bg-brand text-brand-foreground font-medium py-5 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
                onClick={onOpenReservations}
              >
                {t('profile:MY_RESERVATIONS')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GuaranteeChangeNotice;
